import type { IsoDate } from './dates';
import type { OccupancyStatus, StaffPositionStatus } from './enums';

/** FTE зберігається з точністю до сотих; рахуємо в сотих, щоб 0.1 + 0.2 давало 0.3. */
export function fteHundredths(fte: number): number {
  return Math.round(fte * 100);
}

export interface AssignmentSpan {
  fte: number;
  startsAt: IsoDate;
  endsAt: IsoDate | null;
}

/** Чи діє призначення на дату (включно з обома межами). */
export function isActiveOn(a: { startsAt: IsoDate; endsAt: IsoDate | null }, date: IsoDate) {
  return a.startsAt <= date && (a.endsAt === null || a.endsAt >= date);
}

/** Сума FTE призначень, активних на дату. */
export function occupiedFte(assignments: AssignmentSpan[], date: IsoDate): number {
  const total = assignments
    .filter((a) => isActiveOn(a, date))
    .reduce((sum, a) => sum + fteHundredths(a.fte), 0);
  return total / 100;
}

/**
 * Правило 12.4.1. Статус зайнятості штатної одиниці на дату.
 * `suspended` і `closed` ставляться вручну і мають пріоритет; інакше рахується з призначень.
 */
export function occupancyStatus(
  position: { fte: number; status: StaffPositionStatus },
  assignments: AssignmentSpan[],
  date: IsoDate,
): OccupancyStatus {
  if (position.status === 'closed') return 'closed';
  if (position.status === 'suspended') return 'suspended';
  const occupied = fteHundredths(occupiedFte(assignments, date));
  const capacity = fteHundredths(position.fte);
  if (occupied === 0) return 'vacant';
  if (occupied < capacity) return 'partially_occupied';
  if (occupied === capacity) return 'occupied';
  return 'over_occupied';
}

/** Вільна частка FTE на дату (не менше 0). */
export function freeFte(position: { fte: number }, assignments: AssignmentSpan[], date: IsoDate) {
  const free = fteHundredths(position.fte) - fteHundredths(occupiedFte(assignments, date));
  return Math.max(0, free) / 100;
}

/** Максимальне сумарне FTE працівника за замовчуванням. */
export const MAX_EMPLOYEE_FTE = 1;

/**
 * Перевірка навантаження працівника: сума FTE призначень, що перетинаються з новим,
 * у будь-який день його дії. Повертає максимальне навантаження з урахуванням нового.
 * Перевищення `MAX_EMPLOYEE_FTE` — попередження, не заборона.
 */
export function peakEmployeeFte(existing: AssignmentSpan[], next: AssignmentSpan): number {
  // Навантаження змінюється тільки на межах призначень, тож достатньо перевірити ці дати.
  const points = new Set<IsoDate>([next.startsAt]);
  for (const a of existing) {
    if (a.startsAt >= next.startsAt && (next.endsAt === null || a.startsAt <= next.endsAt)) {
      points.add(a.startsAt);
    }
  }
  let peak = 0;
  for (const date of points) {
    peak = Math.max(peak, fteHundredths(occupiedFte([...existing, next], date)));
  }
  return peak / 100;
}

/** Чи перетинаються два періоди (null — безстроково). */
export function periodsOverlap(
  a: { startsAt: IsoDate; endsAt: IsoDate | null },
  b: { startsAt: IsoDate; endsAt: IsoDate | null },
): boolean {
  const aEnd = a.endsAt ?? '9999-12-31';
  const bEnd = b.endsAt ?? '9999-12-31';
  return a.startsAt <= bEnd && b.startsAt <= aEnd;
}

/** Норма годин на місяць за повної зайнятості для оцінки бюджету погодинних посад. */
export const STANDARD_MONTH_HOURS = 168;

/**
 * Кількість місяців у періоді (включно з обома датами), з точністю до сотих:
 * повні календарні місяці + частка неповних за кількістю днів у відповідному місяці.
 */
export function monthsInPeriod(startsAt: IsoDate, endsAt: IsoDate): number {
  if (endsAt < startsAt) return 0;
  const [sy, sm, sd] = startsAt.split('-').map(Number) as [number, number, number];
  const [ey, em, ed] = endsAt.split('-').map(Number) as [number, number, number];
  const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (sy === ey && sm === em) return round2((ed - sd + 1) / daysIn(sy, sm));
  const first = (daysIn(sy, sm) - sd + 1) / daysIn(sy, sm);
  const last = ed / daysIn(ey, em);
  const full = (ey - sy) * 12 + (em - sm) - 1;
  return round2(first + full + last);
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

/**
 * Орієнтовна вартість штатної одиниці за період (мінорні одиниці): місячна ставка × FTE × місяці;
 * погодинна — ставка × норма годин × FTE × місяці. Для оплати за послугу оцінки немає (0).
 * Безстрокова одиниця рахується до кінця проєкту.
 */
export function estimateStaffPositionCost(p: {
  rate: number;
  rateUnit: 'month' | 'hour' | 'service';
  fte: number;
  startsAt: IsoDate;
  endsAt: IsoDate | null;
  projectEndsAt: IsoDate | null;
}): number {
  const end = p.endsAt ?? p.projectEndsAt;
  if (!end || p.rateUnit === 'service') return 0;
  const months = monthsInPeriod(p.startsAt, end);
  const perMonth = p.rateUnit === 'month' ? p.rate : p.rate * STANDARD_MONTH_HOURS;
  return Math.round(perMonth * p.fte * months);
}
